import { Component } from '@theme/component';

/** Breathing room left between the sticky dropdown and a scrolled-to block. */
const SCROLL_GAP = 16;

/**
 * Fabric Selector
 *
 * A sticky selector (vertical list on desktop, dropdown on mobile) that
 * smooth-scrolls to fabric blocks. Each block has a tabbed product carousel:
 * one collection per tab, one Swiper per tab, with a single pair of arrows
 * that drives whichever tab is active. A scroll-spy keeps the active selector
 * item in sync.
 */
class FabricSelector extends Component {
  /** @type {any[]} */
  #swipers = [];
  /** @type {IntersectionObserver | null} */
  #spy = null;
  /** @type {number} */
  #stuckFrame = 0;

  connectedCallback() {
    super.connectedCallback();
    queueMicrotask(() => this.#init());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const s of this.#swipers) {
      try { s?.destroy?.(true, true); } catch (_) {}
    }
    this.#swipers = [];
    this.#spy?.disconnect();
    this.#spy = null;
    window.removeEventListener('scroll', this.#scheduleStuckCheck);
    window.removeEventListener('resize', this.#handleResize);
    cancelAnimationFrame(this.#stuckFrame);
  }

  async #init() {
    this.#initSelector();
    this.#initSpy();
    this.#initStickyWatch();
    await this.#initCarousels();
  }

  /* ---------- Mobile dropdown: is it currently stuck? ----------
     Adds .is-stuck to the host while the sticky dropdown is pinned below the
     header, so CSS can paint the section colour behind the header card. */
  #initStickyWatch() {
    if (!this.querySelector('.fabric-selector__dropdown')) return;
    window.addEventListener('scroll', this.#scheduleStuckCheck, { passive: true });
    window.addEventListener('resize', this.#handleResize, { passive: true });
    this.#scheduleStuckCheck();
    this.#updateScrollOffset();
  }

  #handleResize = () => {
    this.#scheduleStuckCheck();
    this.#updateScrollOffset();
  };

  /* ---------- How much sticky UI sits above a block ----------
     On mobile the header card and the sticky dropdown both overlap the blocks,
     so scrolling a block to the viewport top buries its banner. Publish the
     combined height as --fabric-scroll-offset; .fabric-block reads it as
     scroll-margin-top. Desktop keeps the section's own sticky offset. */
  #updateScrollOffset = () => {
    const dropdown = this.querySelector('.fabric-selector__dropdown');
    const toggle = this.querySelector('.fabric-selector__dropdown-toggle');

    // display:none on desktop — the selector has its own column there.
    if (!(dropdown instanceof HTMLElement) || dropdown.offsetParent === null) {
      this.style.removeProperty('--fabric-scroll-offset');
      return;
    }

    // Measure the toggle, not the dropdown: an open list must not inflate this.
    const styles = getComputedStyle(dropdown);
    const padding =
      (parseFloat(styles.paddingBlockStart) || 0) + (parseFloat(styles.paddingBlockEnd) || 0);
    const toggleHeight = toggle instanceof HTMLElement ? toggle.offsetHeight : 0;

    // header.js hides the header on scroll-down and restores it on scroll-up,
    // so `top` can read near 0 mid-scroll. Reserve the header's resting height
    // as well, or a block scrolled to from below is covered when it returns.
    const headerHeight =
      parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const stickyTop = parseFloat(styles.top) || 0;

    const offset = Math.max(headerHeight, stickyTop) + toggleHeight + padding + SCROLL_GAP;
    this.style.setProperty('--fabric-scroll-offset', `${Math.round(offset)}px`);
  };

  #scheduleStuckCheck = () => {
    cancelAnimationFrame(this.#stuckFrame);
    this.#stuckFrame = requestAnimationFrame(this.#updateStuck);
  };

  #updateStuck = () => {
    const dropdown = this.querySelector('.fabric-selector__dropdown');
    if (!(dropdown instanceof HTMLElement)) return;

    // Hidden on desktop — never report it as stuck there.
    if (dropdown.offsetParent === null) {
      this.classList.remove('is-stuck');
      return;
    }

    const stickyTop = parseFloat(getComputedStyle(dropdown).top) || 0;
    const { top } = dropdown.getBoundingClientRect();
    this.classList.toggle('is-stuck', top <= stickyTop + 1);
  };

  /* ---------- Selector: click → smooth scroll ---------- */
  #initSelector() {
    for (const trigger of this.querySelectorAll('[data-fabric-target]')) {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        const id = trigger.getAttribute('data-fabric-target');
        const target = this.querySelector(`[data-fabric-block="${id}"]`);
        // Close first — scrollIntoView reads the layout as it stands, so any
        // collapsing that happens afterwards would shift the target upward.
        this.#closeDropdown();
        // Re-measure in case the toggle grew since load (font swap, rotation).
        this.#updateScrollOffset();
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    // Mobile dropdown open/close
    const toggle = this.querySelector('[data-fabric-dropdown-toggle]');
    if (toggle) {
      toggle.addEventListener('click', () => {
        this.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', this.classList.contains('is-open') ? 'true' : 'false');
      });
    }
  }

  #closeDropdown() {
    this.classList.remove('is-open');
    const toggle = this.querySelector('[data-fabric-dropdown-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  /** @param {string} id */
  #setActive(id) {
    for (const trigger of this.querySelectorAll('[data-fabric-target]')) {
      trigger.classList.toggle('is-active', trigger.getAttribute('data-fabric-target') === id);
    }
    const label = this.querySelector('[data-fabric-current]');
    const active = this.querySelector(`[data-fabric-target="${id}"]`);
    if (label && active) label.textContent = active.textContent;
  }

  /* ---------- Scroll-spy: highlight the block in view ---------- */
  #initSpy() {
    const blocks = Array.from(this.querySelectorAll('[data-fabric-block]'));
    if (!blocks.length) return;

    this.#spy = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#setActive(entry.target.getAttribute('data-fabric-block') ?? '');
          }
        }
      },
      // Activate when a block sits near the top third of the viewport
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );

    for (const block of blocks) this.#spy.observe(block);
  }

  /* ---------- Product carousels (one per block, one Swiper per tab) ---------- */
  async #initCarousels() {
    const containers = Array.from(this.querySelectorAll('[data-fabric-carousel]'));
    if (!containers.length) return;

    const Swiper = await this.#waitForSwiper();
    if (!Swiper) return;

    for (const container of containers) {
      if (container instanceof HTMLElement) this.#initCarousel(container, Swiper);
    }
  }

  /**
   * Wires one block's carousel: a Swiper per collection panel, tab buttons that
   * swap the visible panel, and a shared prev/next pair that always drives the
   * active panel's Swiper.
   * @param {HTMLElement} container
   * @param {any} Swiper
   */
  #initCarousel(container, Swiper) {
    const panels = Array.from(container.querySelectorAll('[data-fabric-panel]')).filter(
      (el) => el instanceof HTMLElement,
    );
    const tabs = Array.from(container.querySelectorAll('[data-fabric-tab]'));
    const prevEl = container.querySelector('.fabric-block__btn--prev');
    const nextEl = container.querySelector('.fabric-block__btn--next');

    /** @type {Map<string, any>} */
    const swipers = new Map();
    /** @type {any} */
    let active = null;

    // The arrows are shared across tabs, so their state is driven here rather
    // than by Swiper's navigation module (which binds to a single instance).
    const syncArrows = () => {
      const atStart = !active || active.isLocked || active.isBeginning;
      const atEnd = !active || active.isLocked || active.isEnd;
      if (prevEl instanceof HTMLButtonElement) {
        prevEl.classList.toggle('swiper-button-disabled', atStart);
        prevEl.disabled = atStart;
      }
      if (nextEl instanceof HTMLButtonElement) {
        nextEl.classList.toggle('swiper-button-disabled', atEnd);
        nextEl.disabled = atEnd;
      }
    };

    for (const panel of panels) {
      const swiperEl = panel.querySelector('.swiper');
      if (!(swiperEl instanceof HTMLElement) || swiperEl.dataset.swiperInitialized === 'true') continue;

      const instance = new Swiper(swiperEl, {
        // Below the desktop switch the card keeps its fixed mobile width
        // (220px, set in CSS on .swiper-slide); a clean grid of 4 on desktop.
        slidesPerView: 'auto',
        spaceBetween: 8,
        watchOverflow: true,
        a11y: { enabled: true },
        breakpoints: {
          1024: { slidesPerView: 4, spaceBetween: 8 },
        },
        on: {
          slideChange: (/** @type {any} */ sw) => { if (sw === active) syncArrows(); },
          update: (/** @type {any} */ sw) => { if (sw === active) syncArrows(); },
          lock: (/** @type {any} */ sw) => { if (sw === active) syncArrows(); },
          unlock: (/** @type {any} */ sw) => { if (sw === active) syncArrows(); },
        },
      });

      swiperEl.dataset.swiperInitialized = 'true';
      swipers.set(panel.getAttribute('data-fabric-panel') ?? '', instance);
      this.#swipers.push(instance);
    }

    /** @param {string} index */
    const show = (index) => {
      for (const panel of panels) {
        /** @type {HTMLElement} */ (panel).hidden = panel.getAttribute('data-fabric-panel') !== index;
      }
      for (const tab of tabs) {
        const on = tab.getAttribute('data-fabric-tab') === index;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      }
      active = swipers.get(index) ?? null;
      // A panel hidden at init had no layout to measure — do it now it is visible.
      active?.update();
      syncArrows();
    };

    for (const tab of tabs) {
      tab.addEventListener('click', () => show(tab.getAttribute('data-fabric-tab') ?? ''));
    }
    prevEl?.addEventListener('click', () => active?.slidePrev());
    nextEl?.addEventListener('click', () => active?.slideNext());

    const first = panels.find((p) => !(/** @type {HTMLElement} */ (p)).hidden) ?? panels[0];
    if (first) show(first.getAttribute('data-fabric-panel') ?? '');
  }

  /**
   * Wait for the global Swiper to be available.
   * @returns {Promise<any | null>}
   */
  #waitForSwiper(retries = 10, interval = 50) {
    return new Promise((resolve) => {
      const attempt = (remaining) => {
        // @ts-ignore
        const SwiperGlobal = window.Swiper;
        if (SwiperGlobal) return resolve(SwiperGlobal);
        if (remaining <= 0) return resolve(null);
        setTimeout(() => attempt(remaining - 1), interval);
      };
      attempt(retries);
    });
  }
}

if (!customElements.get('fabric-selector')) {
  customElements.define('fabric-selector', FabricSelector);
}
